import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { IcontactFacility, IdashboardItem, Ifacility, Iproduct } from '../../models';
import { PartStatusEnum } from '../../models-enums';
import { IinitialState } from '../../reducers';
import Icon from '../common/Icon';
import SideModal from '../sideModal/SideModal';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatFacilityStreet, streetDisplayLines } from '../../utils/addressUtils';

const facilityMapUrl = (facility: Ifacility) => {
    const { address, address2, city, state, postalCode, countryID } = facility;
    const parts = [formatFacilityStreet(address, address2, countryID), city, state, postalCode].filter(Boolean);
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
};

interface Iprops {
    visible: boolean;
    item: IdashboardItem | undefined;
    onUnderlayClick: () => void;
    onCancel: () => void;
    onPrev: () => void;
    onNext: () => void;
    onOpen: () => void;
    handleStartJob: () => void;
    isPrevDisabled?: boolean;
    isNextDisabled?: boolean;
    isNavigationVisible?: boolean;
    navigationPosition?: { current: number; total: number } | null;
}

const DashboardSidebarWorkOrder: React.FC<Iprops & WithTranslation> = props => {
    const {
        t,
        visible,
        item,
        onUnderlayClick,
        onCancel,
        onPrev,
        onNext,
        onOpen,
        handleStartJob,
        isPrevDisabled,
        isNextDisabled,
        isNavigationVisible,
        navigationPosition
    } = props;

    const workOrder = item?.workOrder;
    const installBase = item?.installBase;
    const facility = item?.facility;
    const country = (facility as any)?.country?.name;
    const product = ((installBase as any)?.material as Iproduct) || { name: 'Unknown' };
    const contacts = (((facility as any)?.contactFacilities as Array<IcontactFacility>) || []).filter(
        x => x?.contact?.firstName || x?.contact?.lastName
    );
    const parts = workOrder?.parts || [];

    if (!item) {
        return null;
    }

    return (
        <SideModal visible={visible} className="dashboard-sidebar-job" onUnderlayClick={onUnderlayClick}>
            <SideModal.Header>
                <div>
                    {navigationPosition && (
                        <span
                            style={{
                                fontSize: '0.65em',
                                verticalAlign: 'baseline',
                                position: 'relative',
                                top: '-2px',
                                color: '#007AC3',
                                fontWeight: '600'
                            }}
                        >
                            ({navigationPosition.current}/{navigationPosition.total}){' '}
                        </span>
                    )}
                    <strong>WO#: {workOrder?.number || '[n/a]'}</strong> - {facility?.name}
                </div>
            </SideModal.Header>
            <SideModal.Body>
                <div>
                    <div className="section-header">{t('manageInventory:productInfo')}</div>
                    <div className="product">{product.name}</div>
                </div>

                {workOrder?.activityDescription && (
                    <div>
                        <div className="section-header">{t('manageWorkOrder:activityDescription')}</div>
                        <div className="section-body">{workOrder.activityDescription}</div>
                    </div>
                )}

                {parts.length > 0 && (
                    <div>
                        <div className="section-header">
                            <div>Parts List</div>
                            <div>
                                {t('manageWorkOrder:status')}
                                {workOrder?.partStatus === PartStatusEnum.Delivered && (
                                    <span className="part-status green" />
                                )}
                                {workOrder?.partStatus === PartStatusEnum.Shipped && (
                                    <span className="part-status yellow" />
                                )}
                                {workOrder?.partStatus === PartStatusEnum.Ordered && (
                                    <span className="part-status red" />
                                )}
                            </div>
                        </div>
                        <table className="part-list">
                            <colgroup>
                                <col />
                                <col />
                                <col />
                                <col />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Ord.</th>
                                    <th>Part #</th>
                                    <th>{t('manageWorkOrder:description')}</th>
                                    <th>Tracking</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parts.map((p) => {
                                    // Get material data from nested material object (populated by backend)
                                    const material = p.material;
                                    const partNumber = material?.number || '';
                                    const partDescription = material?.description || material?.name || '';
                                    
                                    return (
                                        <tr key={p.id}>
                                            <td>{p.estimated}</td>
                                            <td>{partNumber}</td>
                                            <td>
                                                <OverlayTrigger
                                                    placement="top"
                                                    trigger={['hover', 'focus']}
                                                    container={
                                                        document.querySelector(
                                                            '.side-modal.dashboard-sidebar-job'
                                                        ) as HTMLElement
                                                    }
                                                    delay={250}
                                                    popperConfig={{
                                                        modifiers: [
                                                            {
                                                                name: 'computeStyles',
                                                                options: {
                                                                    adaptive: false,
                                                                    enabled: true
                                                                }
                                                            },
                                                            {
                                                                name: 'preventOverflow',
                                                                options: {
                                                                    boundary: 'viewport'
                                                                }
                                                            },
                                                            {
                                                                name: 'custom',
                                                                enabled: true,
                                                                phase: 'beforeWrite',
                                                                fn: ({ state }) => {
                                                                    // Get the description column element
                                                                    const reference = state.elements.reference;
                                                                    if (!('closest' in reference)) return;

                                                                    const row = (reference as Element).closest('tr');
                                                                    const descriptionCell = row?.children[2]; // 3rd column (0-indexed)

                                                                    if (descriptionCell && state.styles.popper) {
                                                                        const containerRect = state.elements.popper.offsetParent?.getBoundingClientRect();

                                                                        if (containerRect) {
                                                                            // Center the tooltip over the description column
                                                                            const cellCenterX = 50;
                                                                            const tooltipWidth =
                                                                                state.elements.popper.offsetWidth;

                                                                            state.styles.popper.left = `${cellCenterX -
                                                                                tooltipWidth / 2}px`;
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        ]
                                                    }}
                                                    overlay={
                                                        <Tooltip
                                                            id={`description-tooltip-${p.id}`}
                                                            className="custom-tooltip"
                                                        >
                                                            {partDescription}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span>{partDescription}</span>
                                                </OverlayTrigger>
                                            </td>
                                            <td>
                                                {p.trackingWebsite ? (
                                                    <a href={p.trackingWebsite} target="_blank" rel="noopener noreferrer">
                                                        link
                                                    </a>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {workOrder?.notes && (
                    <div>
                        <div className="section-header">{t('common:notes')}</div>
                        <div className="section-body notes-body">{workOrder.notes}</div>
                    </div>
                )}

                {installBase?.serialNumber && (
                    <div>
                        <div className="section-header">{t('common:serialNumber')}</div>
                        <div className="section-body">{installBase.serialNumber}</div>
                    </div>
                )}

                {contacts.length > 0 && (
                    <div>
                        <div className="section-header">{t('common:facilityContacts')}</div>
                        <div className="section-body">
                            {contacts.map(fc => {
                                const { contact } = fc;
                                return (
                                    <div key={fc.id}>
                                        {contact?.firstName && <span>{contact.firstName}</span>}
                                        {contact?.lastName && <span> {contact.lastName}</span>}
                                        {contact?.phone && <span>, {contact.phone}</span>}
                                        {contact?.email && <span>, {contact.email}</span>}
                                        {contact?.title && <span>, {contact.title}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div>
                    <div className="section-header">
                        <div>{t('common:facilityDetails')}</div>
                        {facility && (
                            <div>
                                <a href={facilityMapUrl(facility)} target="_blank" rel="noopener noreferrer">
                                    {t('common:viewMap')}
                                </a>
                                <Icon name="map-location-dot" className="px-[20px]" />
                            </div>
                        )}
                    </div>
                    <div className="section-body">
                        {facility?.name || facility?.name2}
                        {streetDisplayLines(facility?.address, facility?.address2, facility?.countryID).map(
                            (line, i) => (
                                <span key={i}>
                                    <br />
                                    {line}
                                </span>
                            )
                        )}
                        {facility?.city && (
                            <span>
                                <br />
                                {facility?.city}
                            </span>
                        )}
                        {facility?.state && <span>, {facility?.state}</span>}
                        {facility?.postalCode && <span> {facility?.postalCode}</span>}
                        {country && (
                            <span>
                                <br />
                                {country}
                            </span>
                        )}
                    </div>
                </div>

                {item?.job?.fseNotes && (
                    <div>
                        <div className="section-header">{t('fseNotes', { ns: 'nsJob' })}</div>
                        <div className="section-body notes-body">{item.job?.fseNotes}</div>
                    </div>
                )}
            </SideModal.Body>
            <SideModal.Footer>
                <Button type="button" onClick={onCancel}>
                    {t('common:cancel')}
                </Button>
                {isNavigationVisible && (
                    <div className="wo-nav-buttons">
                        <Button
                            type="button"
                            onClick={onPrev}
                            title="Prev"
                            aria-label="Prev"
                            variant="default"
                            className="prev"
                            disabled={isPrevDisabled ?? false}
                        >
                            <Icon name="arrow-left" />
                        </Button>
                        <div>WO</div>
                        <Button
                            type="button"
                            onClick={onNext}
                            title="Next"
                            aria-label="Next"
                            variant="default"
                            className="next"
                            disabled={isNextDisabled ?? false}
                        >
                            <Icon name="arrow-right" />
                        </Button>
                    </div>
                )}
                {item?.job ? (
                    <Button type="button" disabled={!item?.job} className="blue" onClick={onOpen}>
                        {t('manageWorkOrder:open')}
                    </Button>
                ) : (
                    <Button type="button" className="blue" onClick={handleStartJob}>
                        {t('common:startJob')}
                    </Button>
                )}
            </SideModal.Footer>
        </SideModal>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    return {};
};

export default withTranslation('dashboard')(connect(mapStateToProps, {})(DashboardSidebarWorkOrder));
